import React, { useCallback, useEffect, useState } from 'react'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks/index'
import Select, { StylesConfig, ValueType } from 'react-select'
import { ChevronDown } from 'react-feather'
import _ from 'lodash'
import {
    openDeleteTagModal,
    openProfileTagModal,
    openTagHistoryModal,
    openTagModal,
    ProfileToTagGroupDetailed,
    ProfileToTagGrouped,
} from 'state/partiposter'
import { PartiposterProfile } from 'state/submissions'
import { theme } from 'theme'
import { selectMultipleTagCategories, selectTagCategory, TagCategoryEnum, TagCategoryTargetEnum } from 'state/constants'
import { Tag } from 'services/api/constants'
import i18n from 'config/i18n'
import { AddTagButton, ProfileTagButton as BaseProfileTagButton, TagButton, ProfileTagButtonProps } from './TagButtons'
import ParticleText from 'particles/Text'
import { fontSize, spacing } from 'particles/styles'
import { coreColors } from 'particles/colors'
import { getSeverityState } from 'particles/Tag'
import { selectCMUserUuid, selectIsInternalUser } from 'state/profile'
import { ClickAwayListener } from '@mui/material'

export const SEVERITY_COLOUR: any = {
    bad: theme.colors.frenchRose,
    average: theme.colors.orange,
    good: theme.colors.jungleGreen,
}

export const TAG_LABELS: any = {
    late_submission_final_step: i18n.t('tag.lateFinalSubmission', 'Late final step submission'),
    late_submission: i18n.t('tag.lateSubmission', 'Late submission'),
    late_post: i18n.t('tag.latePost', 'Late post'),
    early_post: i18n.t('tag.earlyPost', 'Early post'),
    drop_out: i18n.t('tag.dropOut', 'Drop out'),
    bought_followers: i18n.t('tag.boughtFollowers', 'Bought followers'),
    bought_engagement: i18n.t('tag.boughtEngagement', 'Bought engagement'),
    bad_behaviour: i18n.t('tag.badBehaviour', 'Bad behaviour'),
    influencer_type_nano: i18n.t('tag.nano', 'Nano'),
    influencer_type_micro: i18n.t('tag.micro', 'Micro'),
    influencer_type_macro: i18n.t('tag.macro', 'Macro'),
    removeTag: i18n.t('tag.removeTag', 'Remove Tag'),
    photo_quality_bad: i18n.t('tag.badPhotos', 'Bad photos'),
    photo_quality_average: i18n.t('tag.averagePhotos', 'Average photos'),
    photo_quality_good: i18n.t('tag.goodPhotos', 'Good photos'),
    caption_quality_bad: i18n.t('tag.badCaptions', 'Bad captions'),
    caption_quality_average: i18n.t('tag.averageCaptions', 'Average captions'),
    caption_quality_good: i18n.t('tag.goodCaptions', 'Good captions'),
    profile_influencer_type: i18n.t('tag.influencerType', 'Influencer Type'),
    profile_photo_quality: i18n.t('tag.photoQuality', 'Photo Quality'),
    profile_caption_quality: i18n.t('tag.captionQuality', 'Caption Quality'),
    suspended: i18n.t('tag.suspended'),
}

const TagHistoryText = styled(ParticleText)`
    &:hover:not([disabled]) {
        cursor: pointer;
        text-decoration: underline;
    }
`

interface TagComponentProps {
    profileData: PartiposterProfile
    submissionUuid?: string
    campaignUuid?: string
}

export const ShowTagHistoryText: React.FC<TagComponentProps> = ({ profileData, submissionUuid, campaignUuid }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const onOpenTagHistoryModal = useCallback(e => {
        e.stopPropagation()
        dispatch(
            openTagHistoryModal({
                isOpen: true,
                selectedProfile: profileData,
                submissionUuid,
                campaignUuid,
            }),
        )
    }, [])

    if (!profileData.tags_count) {
        return null
    }
    return (
        <Flex>
            <TagHistoryText scale="caption400" onClick={onOpenTagHistoryModal}>
                {newT('tag.showTagHistory', 'Show Tag history >')}
            </TagHistoryText>
        </Flex>
    )
}

const MoreTags = styled(({ className }) => {
    return (
        <div className={className}>
            <Text className="label">...</Text>
        </div>
    )
})`
    display: flex;
    height: 24px;
    border-radius: 4px;
    padding: 0px 4px;
    .label {
        align-self: flex-end;
    }
`

interface SubmissionTagsProps extends TagComponentProps {
    coloredTags?: boolean
    ellipsis?: boolean
    maxDisplay?: number
}

const SubmissionTagContainer = styled.div`
    white-space: nowrap;
`

type PartialProfileToTagGroupDetailed = Omit<ProfileToTagGroupDetailed, 'category'> & {
    category?: ProfileToTagGroupDetailed['category']
}

export const SubmissionTags: React.FC<SubmissionTagsProps> = ({
    profileData,
    submissionUuid,
    campaignUuid,
    coloredTags,
    ellipsis,
    maxDisplay = 5,
}) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const [selectedSubmissionTags, setSelectedSubmissionTags] = useState<PartialProfileToTagGroupDetailed[]>([])
    const tagCategories = useSelector(
        selectMultipleTagCategories([TagCategoryEnum.SUBMISSION_TAG, TagCategoryEnum.PROFILE_TYPE]),
    )

    const onOpenAddTag = useCallback(
        e => {
            e.stopPropagation()
            if (!submissionUuid) {
                return
            }

            dispatch(
                openTagModal({
                    isOpen: true,
                    selectedProfile: profileData,
                    submissionUuid,
                    campaignUuid,
                }),
            )
        },
        [profileData, submissionUuid, campaignUuid],
    )
    useEffect(() => {
        setSelectedSubmissionTags([])

        if (profileData.tags && profileData.tags.length > 0) {
            // assign profile category tag for non submission uuid & campaign uuid
            if (!submissionUuid && !campaignUuid) {
                const tags: PartialProfileToTagGroupDetailed[] = []
                profileData.tags.forEach(value => {
                    const profileTag = value as ProfileToTagGroupDetailed
                    if (!TAG_LABELS[profileTag.name]) {
                        tags.push({
                            ...profileTag,
                            category: {
                                allow_multiple: true,
                                enum: TagCategoryTargetEnum.PROFILE_TYPE,
                                target: TagCategoryTargetEnum.PROFILE_TYPE,
                                uuid: profileTag.uuid,
                            },
                        })
                    } else {
                        Object.values(tagCategories).forEach((categoryTags: any) => {
                            const tagIndex: number = categoryTags.findIndex((tag: Tag) => tag.uuid === profileTag.uuid)
                            if (tagIndex > -1) {
                                tags.push({
                                    ...profileTag,
                                    ...(categoryTags[tagIndex] as Tag),
                                })
                            }
                        })
                    }
                })
                setSelectedSubmissionTags(tags.slice(0, maxDisplay) as ProfileToTagGroupDetailed[])
                return
            }

            const tags: ProfileToTagGroupDetailed[] = []
            profileData.tags.forEach(profileTag => {
                Object.values(tagCategories).forEach((categoryTags: any) => {
                    const tagIndex: number = categoryTags.findIndex((tag: Tag) => tag.uuid === profileTag.uuid)
                    if (tagIndex > -1) {
                        tags.push({
                            ...profileTag,
                            ...(categoryTags[tagIndex] as Tag),
                        })
                    }
                })
            })
            setSelectedSubmissionTags(tags.slice(0, maxDisplay))
        }
    }, [profileData.tags])
    return (
        <SubmissionTagContainer>
            <Flex className="submission-tag">
                {selectedSubmissionTags.length === 0 ? (
                    <Text scale="caption400" color="placeholder">
                        {newT('tag.noTag', 'No tags yet...')}
                    </Text>
                ) : ellipsis ? (
                    <>
                        <TagButton
                            key={0}
                            tagDetail={selectedSubmissionTags[0].latest_tagged_detail}
                            state={coloredTags ? 'hover' : 'default'}
                            maxWidth="160px"
                            contentType={getSeverityState(selectedSubmissionTags[0].severity)}
                            number={selectedSubmissionTags[0].campaign_count}
                            label={
                                selectedSubmissionTags[0].category?.target === TagCategoryTargetEnum.PROFILE_TYPE ||
                                selectedSubmissionTags[0]?.is_custom
                                    ? selectedSubmissionTags[0].name
                                    : TAG_LABELS[selectedSubmissionTags[0].name]
                            }
                        />
                        <Text style={{ marginTop: 4, marginRight: 6 }}>...</Text>
                    </>
                ) : (
                    <>
                        {selectedSubmissionTags.map((tag, index) => (
                            <TagButton
                                key={index}
                                tagDetail={tag.latest_tagged_detail}
                                state={coloredTags ? 'hover' : 'default'}
                                contentType={getSeverityState(tag.severity)}
                                number={tag.campaign_count ?? tag.count}
                                maxWidth="160px"
                                label={
                                    tag.category?.target === TagCategoryTargetEnum.PROFILE_TYPE || tag.is_custom
                                        ? tag.name
                                        : TAG_LABELS[tag.name]
                                }
                            />
                        ))}
                        {profileData.tags_count > maxDisplay && (
                            <MoreTags
                                profileData={profileData}
                                tags={selectedSubmissionTags}
                                coloredTags={coloredTags}
                            />
                        )}
                    </>
                )}

                {submissionUuid && <AddTagButton onClick={onOpenAddTag} />}

                <ShowTagHistoryText
                    profileData={profileData}
                    submissionUuid={submissionUuid}
                    campaignUuid={campaignUuid}
                />
            </Flex>
        </SubmissionTagContainer>
    )
}

interface AccountTagsProps extends Omit<TagComponentProps, 'campaignUuid'> {
    types: {
        influencer?: boolean
        photo?: boolean
        caption?: boolean
    }
}

export const AccountTags: React.FC<AccountTagsProps> = ({
    profileData,
    submissionUuid,
    types = { influencer: false, photo: false, caption: false },
}) => {
    const { influencer, photo, caption } = types
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const onOpenProfileTagModal = useCallback(
        (selectedTag: Tag, categoryEnum: TagCategoryEnum, uuid: string | null) => {
            if (!submissionUuid) {
                return
            }
            dispatch(
                openProfileTagModal({
                    isOpen: true,
                    selectedProfile: profileData,
                    submissionUuid,
                    selectedTag,
                    categoryEnum,
                    uuid,
                }),
            )
        },
        [profileData],
    )
    const onDeleteProfileTagModal = useCallback(
        (selectedTag: Tag, uuid: string) => {
            if (!submissionUuid) {
                return
            }
            dispatch(
                openDeleteTagModal({
                    isOpen: true,
                    selectedProfile: profileData,
                    submissionUuid,
                    selectedTag,
                    uuid,
                }),
            )
        },
        [profileData],
    )

    const influencerType = useSelector(selectTagCategory(TagCategoryEnum.INFLUENCER_TYPE))
    const photoQuality = useSelector(selectTagCategory(TagCategoryEnum.PHOTO_QUALITY))
    const captionQuality = useSelector(selectTagCategory(TagCategoryEnum.CAPTION_QUALITY))
    const [selectedInfluencerType, setSelectedInfluencerType] = useState<ProfileToTagGrouped | null>(null)
    const [selectedPhotoQuality, setSelectedPhotoQuality] = useState<ProfileToTagGrouped | null>(null)
    const [selectedCaptionQuality, setSelectedCaptionQuality] = useState<ProfileToTagGrouped | null>(null)

    useEffect(() => {
        setSelectedInfluencerType(null)
        setSelectedPhotoQuality(null)
        setSelectedCaptionQuality(null)
        if (profileData.tags && profileData.tags.length > 0) {
            profileData.tags.forEach(profileTag => {
                if (influencer && influencerType?.tags?.some(tag => tag.uuid === profileTag.uuid)) {
                    setSelectedInfluencerType(profileTag)
                } else if (photo && photoQuality?.tags?.some(tag => tag.uuid === profileTag.uuid)) {
                    setSelectedPhotoQuality(profileTag)
                } else if (caption && captionQuality?.tags?.some(tag => tag.uuid === profileTag.uuid)) {
                    setSelectedCaptionQuality(profileTag)
                }
            })
        }
    }, [profileData.tags])
    return (
        <Flex className="account-tag">
            {influencer && (
                <ProfileTag
                    onChange={(option: Tag, uuid: string | null): void => {
                        onOpenProfileTagModal(option, influencerType.enum as TagCategoryEnum, uuid)
                    }}
                    onRemoveTag={onDeleteProfileTagModal}
                    options={influencerType?.tags}
                    value={selectedInfluencerType}
                />
            )}
            {photo && (
                <>
                    <ProfileTag
                        onChange={(option: Tag, uuid: string | null): void => {
                            onOpenProfileTagModal(option, photoQuality.enum as TagCategoryEnum, uuid)
                        }}
                        onRemoveTag={onDeleteProfileTagModal}
                        placeholder={newT('tag.photoPlaceholder', 'Tag photos as...')}
                        options={photoQuality?.tags}
                        value={selectedPhotoQuality}
                    />
                </>
            )}
            {caption && (
                <ProfileTag
                    onChange={(option: Tag, uuid: string | null): void => {
                        onOpenProfileTagModal(option, captionQuality.enum as TagCategoryEnum, uuid)
                    }}
                    onRemoveTag={onDeleteProfileTagModal}
                    placeholder={newT('tag.captionPlaceholder', 'Tag captions as...')}
                    options={captionQuality?.tags}
                    value={selectedCaptionQuality}
                />
            )}
        </Flex>
    )
}

const customStyles: StylesConfig = {
    control: provided => ({ ...provided, display: 'none' }),
    menu: (provided, state) => ({
        ...provided,
        width: 'max-content',
        boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)',
        margin: 0,
    }),
    menuList: (provided, state) => ({
        ...provided,
        border: `1px solid ${coreColors.blue300}`,
        borderRadius: 5,
    }),
    option: (provided, state) => ({
        ...provided,
        color:
            state.value === 'removeTag'
                ? theme.colors.frenchRose
                : state.isSelected
                  ? theme.newColors.neutral50
                  : theme.colors.governorBay,
        textAlign: 'left',
        fontSize: fontSize.xs,
        borderTop: `${state.value === 'removeTag' ? '1px' : 0} solid black`,
    }),
}

interface ProfileTagProps {
    options: Tag[]
    placeholder?: string
    width?: any
    onChange: (option: Tag, uuid: string | null) => void
    onRemoveTag: (option: Tag, uuid: string) => void
    getOptionLabel?: (option: Tag) => string
    getOptionValue?: (option: Tag) => string
    value: ProfileToTagGrouped | null
}

const ProfileTagButton = styled(BaseProfileTagButton)<ProfileTagButtonProps>`
    border-radius: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 2px 4px;
    font-size: 0.6em;
    border: 0;
`

const DropdownMenu = styled.div`
    position: absolute;
    z-index: 2;
`

const DropdownBlanket = styled.div`
    bottom: 0;
    left: 0;
    top: 0;
    right: 0;
    position: fixed;
    z-index: 1;
`
interface FlexProps {
    mt?: string
    mb?: string
}
const Flex = styled.div<FlexProps>`
    box-sizing: border-box;
    margin: 0;
    min-width: 0;
    display: flex;
    &.submission-tag {
        align-items: center;
        flex-wrap: wrap;
        gap: ${spacing[100]};
    }
    &.account-tag {
        gap: ${spacing[200]};
    }
    &.button-texts {
        gap: ${spacing[100]};
    }
`

const ProfileTag: React.FC<ProfileTagProps> = ({
    options,
    placeholder = '...',
    onChange,
    onRemoveTag,
    getOptionLabel = (option: Tag): string => TAG_LABELS[option.name],
    getOptionValue = (option: Tag): string => option.name,
    value,
}) => {
    const cmUserUuid = useSelector(selectCMUserUuid)
    const isInternalUser = useSelector(selectIsInternalUser)
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const addRemoveTag = isInternalUser
        ? !_.isEmpty(value)
        : !_.isEmpty(value) && cmUserUuid === value?.latest_tagged_detail?.creator_user_info?.uuid

    const removeTag: Tag = {
        uuid: '',
        category: {
            allow_multiple: false,
            enum: '',
            target: '',
            uuid: '',
        },
        name: 'removeTag',
        is_custom: false,
        severity: 'average',
    }
    const toggleIsDropdownOpen = useCallback(() => {
        setIsDropdownOpen(!isDropdownOpen)
    }, [setIsDropdownOpen, isDropdownOpen])
    const selectedOption = value ? options.find((option: Tag) => option.uuid === value.uuid) : null
    const isTagDisabled =
        !isInternalUser && value !== null && value?.latest_tagged_detail?.last_updater_user_info?.uuid !== cmUserUuid

    return (
        <div onClick={toggleIsDropdownOpen}>
            <ProfileTagButton
                LeftIcon={null}
                {...(selectedOption ? { severity: selectedOption.severity } : null)}
                RightIcon={
                    <div style={{ marginLeft: theme.newSpacing.xxxxs }}>
                        <ChevronDown size={8} strokeWidth={4} />
                    </div>
                }
                disabled={isTagDisabled}
            >
                <div>{selectedOption ? TAG_LABELS[selectedOption.name] : placeholder}</div>
            </ProfileTagButton>
            {isDropdownOpen ? (
                <ClickAwayListener onClickAway={toggleIsDropdownOpen}>
                    <DropdownMenu>
                        <Select
                            autoFocus
                            backspaceRemovesValue={false}
                            controlShouldRenderValue={false}
                            hideSelectedOptions={false}
                            isClearable={false}
                            menuIsOpen
                            tabSelectsValue={false}
                            components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                            options={[...options, ...(addRemoveTag ? [removeTag] : [])]}
                            styles={customStyles}
                            fontSize={fontSize.xs}
                            color={coreColors.white50}
                            isSearchable={false}
                            onChange={(option: ValueType<Tag>) => {
                                const changeOption = option as Tag
                                if (changeOption) {
                                    if (changeOption.name === 'removeTag') {
                                        value &&
                                            value.latest_tagged_detail &&
                                            selectedOption &&
                                            onRemoveTag(selectedOption, value.latest_tagged_detail.uuid)
                                    } else if (!value || changeOption.uuid !== value.uuid) {
                                        onChange(
                                            changeOption,
                                            value && value.latest_tagged_detail
                                                ? value.latest_tagged_detail.uuid
                                                : null,
                                        )
                                    }
                                    toggleIsDropdownOpen()
                                }
                            }}
                            getOptionLabel={getOptionLabel}
                            getOptionValue={getOptionValue}
                            value={selectedOption}
                        />
                    </DropdownMenu>
                </ClickAwayListener>
            ) : null}
            {isDropdownOpen ? <DropdownBlanket onClick={toggleIsDropdownOpen} /> : null}
        </div>
    )
}
